import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { STATUSES, useInspections, type InspectionListItem } from '../../api/inspections';
import { useEnumLabel, useEnumOptions, useEnumParent } from '../../api/enums';
import { useIsAdmin } from '../../api/me';
import { api } from '../../api/client';
import type { Page } from '../../api/types';
import type { Subject } from '../../api/subjects';
import { Autocomplete, Button, ButtonGroup, ButtonLink, DateInput, EMPTY_DATE, ErrorSummary, Input, Pagination, Select, Table, isoToParts, partsToIso, type DateParts } from '../../components/idsk';
import { PageHeading } from '../../layout/PageHeading';
import { usePageTitle } from '../../layout/usePageTitle';
import { useAnnouncer } from '../../layout/Announcer';
import { useListParams } from '../../lib/useListParams';
import { formatDate } from '../../lib/format';
import { QueryError } from '../common/QueryError';
import { Loading } from '../common/LoadingPage';
import { StatusTag } from './StatusTag';

const FILTERS = ['q', 'status', 'type', 'task', 'subjectId', 'subjectName', 'from', 'to'] as const;

export function InspectionTable({ rows, caption, sort, onSort }: { rows: InspectionListItem[]; caption: string; sort?: { key: string; direction: 'asc' | 'desc' }; onSort?: (key: string) => void }) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  return (
    <Table<InspectionListItem>
      caption={caption}
      rows={rows}
      rowKey={(i) => i.id}
      sort={sort}
      onSort={onSort}
      columns={[
        { key: 'number', header: t('inspections.fields.number'), sortable: true, rowHeader: true, render: (i) => <Link className="govuk-link" to={`/inspekcie/${i.id}`}>{i.number}</Link> },
        { key: 'name', header: t('inspections.fields.name'), sortable: true, render: (i) => i.name },
        { key: 'subject', header: t('inspections.fields.subject'), sortable: true, render: (i) => i.subject?.name ?? '' },
        {
          key: 'type',
          header: t('inspections.fields.typeAndTask'),
          sortable: true,
          render: (i) => (
            <>
              {label('inspectionTypes', i.type)}
              {i.task && (
                <>
                  <br />
                  <span className="govuk-hint govuk-!-font-size-16 govuk-!-margin-bottom-0">{t('inspections.taskCode', { code: i.task })}</span>
                </>
              )}
            </>
          ),
        },
        { key: 'dateFrom', header: t('inspections.fields.timeframe'), sortable: true, render: (i) => t('wizard.step4.range', { from: formatDate(i.dateFrom), to: formatDate(i.dateTo) }) },
        { key: 'status', header: t('inspections.fields.status'), sortable: true, render: (i) => <StatusTag status={i.status} /> },
        { key: 'leader', header: t('inspections.fields.leader'), render: (i) => i.leaderName ?? '' },
      ]}
      emptyText={t('inspections.empty')}
    />
  );
}

export function InspectionsListPage() {
  const { t } = useTranslation();
  const admin = useIsAdmin();
  const { announce } = useAnnouncer();
  const statusOptions = useEnumOptions('inspectionStatuses');
  const typeOptions = useEnumOptions('inspectionTypes');
  const typeOf = useEnumParent();
  const list = useListParams(FILTERS, 'dateFrom,desc');
  const [draft, setDraft] = useState({
    q: list.filters.q,
    status: list.filters.status,
    type: list.filters.type,
    task: list.filters.task,
    subjectId: list.filters.subjectId,
    subjectName: list.filters.subjectName,
  });
  // the tasks of the selected type; all of them, grouped by type, until a type is selected
  const taskOptions = useEnumOptions('inspectionTasks', draft.type || undefined);
  const [from, setFrom] = useState<DateParts>(isoToParts(list.filters.from));
  const [to, setTo] = useState<DateParts>(isoToParts(list.filters.to));
  const [dateError, setDateError] = useState<string | null>(null);
  const query = useInspections({
    q: list.filters.q || undefined,
    status: list.filters.status || undefined,
    type: list.filters.type || undefined,
    task: list.filters.task || undefined,
    subjectId: list.filters.subjectId || undefined,
    from: list.filters.from || undefined,
    to: list.filters.to || undefined,
    page: list.page,
    size: 20,
    sort: list.sort,
  });
  usePageTitle([t('inspections.title')]);

  const apply = (e: FormEvent) => {
    e.preventDefault();
    const f = partsToIso(from);
    const tt = partsToIso(to);
    if (f.kind === 'invalid' || tt.kind === 'invalid') {
      setDateError(t('inspections.filters.invalidDate'));
      return;
    }
    setDateError(null);
    list.update({ ...draft, from: f.kind === 'valid' ? f.iso : null, to: tt.kind === 'valid' ? tt.iso : null, page: null });
  };
  const clear = () => {
    setDraft({ q: '', status: '', type: '', task: '', subjectId: '', subjectName: '' });
    setFrom({ ...EMPTY_DATE });
    setTo({ ...EMPTY_DATE });
    setDateError(null);
    list.update({ q: null, status: null, type: null, task: null, subjectId: null, subjectName: null, from: null, to: null, page: null });
  };
  const sortLabels: Record<string, string> = {
    number: t('inspections.fields.number'),
    name: t('inspections.fields.name'),
    subject: t('inspections.fields.subject'),
    type: t('inspections.fields.typeAndTask'),
    dateFrom: t('inspections.fields.timeframe'),
    status: t('inspections.fields.status'),
  };

  return (
    <>
      <div className="govuk-grid-row">
        <div className="govuk-grid-column-two-thirds">
          <PageHeading>{t('inspections.title')}</PageHeading>
        </div>
        {admin && (
          <div className="govuk-grid-column-one-third">
            <ButtonLink to="/inspekcie/nova">{t('inspections.add')}</ButtonLink>
          </div>
        )}
      </div>
      {dateError && <ErrorSummary errors={[{ targetId: 'filter-from-day', text: dateError }]} />}
      <form className="app-filters" role="search" aria-label={t('inspections.filters.label')} onSubmit={apply} noValidate>
        <h2 className="govuk-heading-s">{t('inspections.filters.heading')}</h2>
        <div className="govuk-grid-row">
          <div className="govuk-grid-column-one-half">
            <Input id="filter-q" label={t('inspections.filters.q')} value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} />
          </div>
          <div className="govuk-grid-column-one-quarter">
            <Select id="filter-status" label={t('inspections.fields.status')} options={statusOptions} placeholder={t('common.all')} value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} width="full" />
          </div>
          <div className="govuk-grid-column-one-quarter">
            <Select
              id="filter-type"
              label={t('inspections.fields.type')}
              options={typeOptions}
              placeholder={t('common.all')}
              value={draft.type}
              onChange={(e) => {
                const type = e.target.value;
                // a task of another type no longer applies
                setDraft({ ...draft, type, task: !type || typeOf('inspectionTasks', draft.task) === type ? draft.task : '' });
              }}
              width="full"
            />
          </div>
        </div>
        <Select id="filter-task" label={t('inspections.fields.task')} options={taskOptions} placeholder={t('common.all')} value={draft.task} onChange={(e) => setDraft({ ...draft, task: e.target.value })} width="full" />
        <Autocomplete<Subject>
          id="filter-subject"
          label={t('inspections.filters.subject')}
          hint={draft.subjectName ? t('inspections.filters.subjectSelected', { name: draft.subjectName }) : t('inspections.filters.subjectHint')}
          initialText={draft.subjectName}
          search={(q, signal) => api<Page<Subject>>('/subjects', { query: { q, page: 0, size: 10, sort: 'name,asc' }, signal }).then((p) => p.content)}
          getOptionKey={(s) => s.id}
          getOptionLabel={(s) => s.name}
          getOptionHint={(s) => `${s.eduid}, ${s.address.municipality}`}
          onSelect={(s) => setDraft({ ...draft, subjectId: s.id, subjectName: s.name })}
        />
        <div className="govuk-grid-row">
          <div className="govuk-grid-column-one-half">
            <DateInput id="filter-from" legend={t('inspections.filters.from')} legendClassName="govuk-fieldset__legend--s" value={from} onChange={setFrom} error={dateError ?? undefined} />
          </div>
          <div className="govuk-grid-column-one-half">
            <DateInput id="filter-to" legend={t('inspections.filters.to')} legendClassName="govuk-fieldset__legend--s" value={to} onChange={setTo} />
          </div>
        </div>
        <ButtonGroup>
          <Button type="submit">{t('common.applyFilters')}</Button>
          <Button variant="secondary" onClick={clear}>
            {t('common.clearFilters')}
          </Button>
        </ButtonGroup>
      </form>
      {query.error ? (
        <QueryError error={query.error} />
      ) : query.isPending ? (
        <Loading />
      ) : (
        <>
          <InspectionTable
            rows={query.data.content}
            caption={t('inspections.tableCaption')}
            sort={list.sortState}
            onSort={(key) => {
              const direction = list.toggleSort(key);
              announce(t('table.sortedBy', { column: sortLabels[key], direction: t(`table.${direction}`) }));
            }}
          />
          <Pagination page={query.data.page} totalPages={query.data.totalPages} totalElements={query.data.totalElements} onPage={(p) => list.update({ page: p })} />
        </>
      )}
    </>
  );
}

export { STATUSES };
